import { Outlet, Link } from "react-router-dom";
import { AlertTriangle, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { CookieNotice } from "@/components/CookieNotice";
import { StickyAreaCta } from "@/components/StickyAreaCta";
import { Toaster } from "sonner";
import { useApp } from "@/context/AppContext";
import { useAreaWatch } from "@/lib/useAreaWatch";
import { ThreatLevelChip } from "@/components/ThreatLevelChip";
import { PlanSaveReminder } from "@/components/plan/PlanSaveReminder";

const CTA = { gov_alert: "What should I do?", threat: "What to do in an attack", flood: "Flood advice", weather: "Is my area affected?" };

export default function Layout() {
  const { emergency, t } = useApp();
  useAreaWatch();
  return (
    <div className="min-h-screen flex flex-col">
      {/* Independence disclaimer */}
      <div data-testid="disclaimer-bar" data-print-hide className="bg-navy text-slate-100 text-[11px] sm:text-xs py-2 px-4 text-center border-b border-slate-800 font-medium">
        {t("disclaimer")}<ThreatLevelChip />
      </div>

      <Header />
      <PlanSaveReminder />

      {emergency.active && (
        <div data-testid="emergency-banner" className={emergency.auto && emergency.level === "AMBER" ? "bg-amber-500 text-navy" : "bg-red-700 text-white"}>
          <div className="container-x py-3 sm:py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-heading font-extrabold text-base sm:text-lg tracking-wide">{emergency.banner || "EMERGENCY INFORMATION"}</div>
                {emergency.headline && <div className="font-semibold mt-0.5">{emergency.headline}{emergency.area ? ` — ${emergency.area}` : ""}</div>}
                {emergency.advice && <div className={`text-sm mt-1 ${emergency.auto && emergency.level === "AMBER" ? "" : "text-red-50"}`}>{emergency.advice}</div>}
                <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                  <Link to={emergency.link || "/emergency"} data-testid="emergency-banner-cta" className="rounded bg-white text-red-800 font-bold px-3 py-1.5 shadow-sm">{CTA[emergency.kind] || "What should I do?"}</Link>
                  <Link to="/live" className="underline">UK Situation Room</Link>
                  {emergency.kind && emergency.kind !== "weather" && <Link to="/emergency" className="underline">Emergency guidance</Link>}
                  {emergency.source_url && <a href={emergency.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline">Official source <ExternalLink className="h-3.5 w-3.5" /></a>}
                  {emergency.updated_at && <span className="text-xs font-mono opacity-80">{emergency.kind === "threat" ? "Set by UK Ready" : emergency.auto ? "Official source checked" : "Last verified"} {new Date(emergency.updated_at).toLocaleString("en-GB", { timeZone: "Europe/London" })}</span>}
                </div>
                {emergency.also?.length > 0 && <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold" data-testid="emergency-banner-also">Also in force: {emergency.also.map((x) => <Link key={x.banner} to={x.link} className="underline">{x.banner}</Link>)}</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 pb-20 md:pb-0">
        <Outlet />
      </main>

      <Footer />
      <StickyAreaCta />
      <CookieNotice />
      <Toaster position="bottom-center" richColors />
    </div>
  );
}
